import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import keycode from 'keycode';
import styled from 'styled-components';
import { Icon } from '@codex/icons';

const List = styled.ul`
  position: absolute;
  z-index: 3;
  top: 50px;
  left: 0;
  right: 0;
  margin: 0;
  overflow: hidden;
  list-style: none;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 0 0 40px;
  background: #fff
    url('https://developers.google.com/places/documentation/images/powered-by-google-on-white.png')
    no-repeat bottom 10px left 10px;

  /** ==================== Waiho's funky - Suggestion List Title Block! ================ **/
  &:not([data-label='']):before {
    content: attr(data-label);
    margin: 10px 10px 0 10px;
    display: block;
    color: #666666;
    position: absolute;
    line-height: 11px;
    font-size: 11px;
    z-index: 2;
  }

  &:not([data-label='']):after {
    content: '';
    display: block;
    position: absolute;
    border-bottom: 1px solid #cccccc;
    top: 15px;
    width: 67%;
    right: 0;
  }

  &:not([data-label='']) > li:first-of-type {
    margin-top: 15px;
  }
`;

const ListItem = styled.li`
  position: relative;
  padding: 15px 10px 10px;
  border-bottom: 1px solid #ccc;
  color: #666;
  cursor: pointer;
  background-color: white;
  list-style: none;
  font-size: 14px;
  outline: 0;

  ${({ selected }) => selected && 'background-color: #f2f2f2'};
`;

const HeadingText = styled.span`
  display: block;
  white-space: overflow;
  text-overflow: ellipsis;
  margin-bottom: 5px;
`;

const SubheadingText = styled.span`
  display: block;
  white-space: overflow;
  text-overflow: ellipsis;
  font-size: 14px;
  text-align: right;
  color: #999;
`;

const IconContainer = styled.span`
  position: absolute;
  top: 5px;
  right: 10px;
`;

export default class LocationsList extends PureComponent {
  static getDerivedStateFromProps(nextProps, prevState) {
    const hoveredByDefaultLocationIndex = nextProps.locations.findIndex(
      (location) => location.hoveredByDefault
    );

    // Setting default hovered element if no other element is hovered
    if (hoveredByDefaultLocationIndex !== -1 && prevState.hoveredLocationIndex === -1) {
      return {
        hoveredLocationIndex: hoveredByDefaultLocationIndex
      };
    }

    return null;
  }

  constructor(props) {
    super(props);

    this.onListItemPressed = this.onListItemPressed.bind(this);
    this.onListItemMouseEnter = this.onListItemMouseEnter.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.state = {
      hoveredLocationIndex: -1
    };
  }

  componentDidMount() {
    document.addEventListener('keydown', this.onKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
  }

  onListItemPressed(e) {
    const { locations } = this.props;

    e.preventDefault();

    this.props.onListItemSelected(locations[Number(e.currentTarget.dataset.index)]);
  }

  onListItemEnterKeyPressed(e) {
    const { locations } = this.props;

    this.props.onListItemSelected(locations[Number(this.state.hoveredLocationIndex)]);
  }

  onListItemMouseEnter(e) {
    if (!e.currentTarget.dataset.selected) {
      const indexOfHoveredListItem = Number(e.currentTarget.dataset.index);
      const { locations } = this.props;

      this.setState(
        {
          hoveredLocationIndex: indexOfHoveredListItem
        },
        () => {
          this.props.onListItemHovered(locations[Number(this.state.hoveredLocationIndex)]);
        }
      );
    }
  }

  onKeyDown(e) {
    const currentKey = e.keyCode;
    const { locations } = this.props;
    const { hoveredLocationIndex } = this.state;

    if (
      currentKey === keycode.codes.up ||
      currentKey === keycode.codes.down ||
      currentKey === keycode.codes.enter
    ) {
      e.preventDefault();
    }

    switch (currentKey) {
      case keycode.codes.up: {
        const newHoveredLocationIndex =
          hoveredLocationIndex === 0 || hoveredLocationIndex === -1
            ? locations.length - 1
            : hoveredLocationIndex - 1;

        this.setState(
          {
            hoveredLocationIndex: newHoveredLocationIndex
          },
          () => {
            this.props.onListItemHovered(locations[Number(this.state.hoveredLocationIndex)]);
          }
        );
        break;
      }
      case keycode.codes.down: {
        const newHoveredLocationIndex =
          hoveredLocationIndex === locations.length - 1 || hoveredLocationIndex === -1
            ? 0
            : hoveredLocationIndex + 1;

        this.setState(
          {
            hoveredLocationIndex: newHoveredLocationIndex
          },
          () => {
            this.props.onListItemHovered(locations[Number(this.state.hoveredLocationIndex)]);
          }
        );
        break;
      }
      case keycode.codes.enter: {
        if (hoveredLocationIndex !== -1) {
          this.onListItemEnterKeyPressed();
        }
        break;
      }
      default: {
        break;
      }
    }
  }

  renderItems() {
    return this.props.locations.map((location, i) => (
      <ListItem
        className={'dc-locations-list-item'}
        key={location.id}
        onMouseDown={this.onListItemPressed}
        onTouchEnd={this.onListItemPressed}
        onMouseEnter={this.onListItemMouseEnter}
        role={'option'}
        data-index={i}
        aria-selected={this.state.hoveredLocationIndex === i ? true : undefined}
        data-selected={this.state.hoveredLocationIndex === i ? true : undefined}
        selected={this.state.hoveredLocationIndex === i}
        tabIndex={0}
      >
        <HeadingText className={'heading-text'}>{location.location}</HeadingText>

        <SubheadingText className={'subheading-text'}>
          {`  ${location.description}`}
        </SubheadingText>

        {location.retailLocation && (
        <IconContainer className={'icon-container'}>
          <Icon icon={'House'} />
        </IconContainer>
        )}
      </ListItem>
    ));
  }

  render() {
    const listClasses = ['dc-locations-list'];

    return (
      <List className={listClasses.join(' ')} data-label={this.props.headerLabel} role={'listbox'}>
        {this.renderItems()}
      </List>
    );
  }
}

LocationsList.displayName = 'LocationsList';

LocationsList.propTypes = {
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
      retailLocation: PropTypes.bool,
      hoveredByDefault: PropTypes.bool
    })
  ).isRequired,
  headerLabel: PropTypes.string,
  onListItemSelected: PropTypes.func,
  onListItemHovered: PropTypes.func
};

LocationsList.defaultProps = {
  headerLabel: '',
  onListItemSelected: () => {},
  onListItemHovered: () => {}
};
